import React, { useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import API_URL from './api';
import './App.css';
import { ToastProvider } from './contexts/ToastContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import Sidebar from './components/Sidebar.js';
import Dashboard from './components/Dashboard.js';
import StudentTest from './components/StudentTest.js';
import TestResults from './components/TestResults.js';
import QuizBuilder from './components/QuizBuilder.js';
import QuizPlay from './components/QuizPlay.js';
import UserManager from './components/UserManager.js';
import MultiDeskTest from './components/MultiDeskTest.js';
import ComplexTestBuilder from './components/ComplexTestBuilder.js';
import InactiveTest from './components/InactiveTest.js';
import CityManagement from './components/CityManagement.js';
import Login from './components/Login.js';
import GamePlay from './components/GamePlay.js';
import Analytics from './components/Analytics.js';
import DishManagement from './components/DishManagement.js';
import Settings from './components/Settings';
import MenuManagement from './components/MenuManagement';
import StudentManager from './components/StudentManager';
import StudentProfile from './components/StudentProfile';
import PizzaTestPlay from './components/PizzaTestPlay';
import StudentRegister from './components/StudentRegister';
import StudentDashboard from './components/StudentDashboard';
import StoreManager from './components/StoreManager';
import AssignmentManager from './components/AssignmentManager';
import DepartmentManager from './components/DepartmentManager';
import CertificateManager from './components/CertificateManager';
import CertificateVerify from './components/CertificateVerify';
import CertificateLookup from './components/CertificateLookup';
import CourseManager from './components/CourseManager';
import CoursePlayer from './components/CoursePlayer';
import Library from './components/Library';
import UserGuide from './components/UserGuide';
import SubTabBar from './components/SubTabBar';
import BuildersHub from './components/BuildersHub';

// Lazy-loaded heavy components for optimal bundle splitting
const VirtualDesk = lazy(() => import('./components/VirtualDesk.js'));
const VisualGameBuilder = lazy(() => import('./components/VisualGameBuilder.js'));
const ComplexTestPlay = lazy(() => import('./components/ComplexTestPlay.js'));
const PairingBuilder = lazy(() => import('./components/PairingBuilder'));



function AdminPanel({ onLogout, user, theme, toggleTheme, hiddenItems }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(
    () => localStorage.getItem('sidebar-collapsed') === 'true'
  );

  const toggleCollapse = () => {
    const nextState = !isCollapsed;
    setIsCollapsed(nextState);
    localStorage.setItem('sidebar-collapsed', String(nextState));
  };

  const pathTab = location.pathname.split('/')[1] || 'dashboard';

  let resolvedTab = pathTab;
  if (['students', 'test-results', 'assignments'].includes(pathTab)) {
    resolvedTab = 'students';
  } else if (['courses', 'library', 'builders', 'quiz-builder', 'visual-builder', 'virtual-desk', 'complex-builder', 'certificates', 'pairing-builder'].includes(pathTab)) {
    resolvedTab = 'courses';
  } else if (['admin', 'users', 'cities', 'departments', 'menu-management', 'dishes', 'settings'].includes(pathTab)) {
    resolvedTab = 'admin';
  }

  const activeTab = user?.role === 'localadmin' ? 'analytics' : resolvedTab;

  let activeSubTabs = [];
  let showSubTabs = false;
  
  if (['students', 'test-results', 'assignments'].includes(pathTab)) {
    activeSubTabs = [
      { path: '/students', label: '👥 Список гравців', id: 'students' },
      { path: '/test-results', label: '📊 Результати тестів', id: 'test-results' },
      { path: '/assignments', label: '🎯 Менеджер призначень', id: 'assignments' }
    ].filter(tab => !hiddenItems.includes(tab.id));
    showSubTabs = ['superadmin', 'admin', 'trainer'].includes(user?.role);
  } else if (['courses', 'library', 'builders', 'quiz-builder', 'visual-builder', 'virtual-desk', 'complex-builder', 'certificates', 'pairing-builder'].includes(pathTab)) {
    activeSubTabs = [
      { path: '/courses', label: '📖 Курси навчання', id: 'courses' },
      { path: '/builders', label: '⚡ Інтерактивність', id: 'builders' },
      { path: '/library', label: '📂 Бібліотека медіа', id: 'library' }
    ].filter(tab => !hiddenItems.includes(tab.id));
    showSubTabs = ['superadmin', 'admin', 'trainer'].includes(user?.role);
  } else if (['users', 'cities', 'departments', 'menu-management', 'dishes', 'settings'].includes(pathTab)) {
    activeSubTabs = [
      { path: '/users', label: '👥 Користувачі', role: 'superadmin', id: 'users' },
      { path: '/cities', label: '🏙️ Міста', role: 'superadmin', id: 'cities' },
      { path: '/departments', label: '🏢 Підрозділи', role: 'superadmin', id: 'departments' },
      { path: '/menu-management', label: '📋 Управління меню', role: 'superadmin', id: 'menu-management' },
      { path: '/dishes', label: '🍽️ База посуду', role: ['superadmin', 'admin'], id: 'dishes' },
      { path: '/settings', label: '⚙️ Обслуговування', role: ['superadmin', 'admin'], id: 'settings' }
    ].filter(tab => {
      if (hiddenItems.includes(tab.id)) return false;
      if (Array.isArray(tab.role)) return tab.role.includes(user?.role);
      return !tab.role || tab.role === user?.role;
    });
    showSubTabs = ['superadmin', 'admin'].includes(user?.role);
  }

  return (
    <div className={`admin-container ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <button className="mobile-toggle" onClick={() => setSidebarOpen(true)}>☰</button>
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>}
      <Sidebar
        activeTab={activeTab}
        setActiveTab={(tab) => {
          if (tab === 'students') navigate('/students');
          else if (tab === 'courses') navigate('/courses');
          else if (tab === 'builders') navigate('/builders');
          else if (tab === 'admin') {
            if (user?.role === 'superadmin') navigate('/users');
            else navigate('/dishes');
          }
          else navigate(`/${tab === 'dashboard' ? '' : tab}`);
        }}
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
        isCollapsed={isCollapsed}
        onToggleCollapse={toggleCollapse}
        onLogout={onLogout}
        user={user}
        theme={theme}
        toggleTheme={toggleTheme}
        hiddenItems={hiddenItems}
      />
      <main className="admin-main">
        {showSubTabs && <SubTabBar tabs={activeSubTabs} />}
        <Suspense fallback={<div className="dashboard-loading"><div className="loading-spinner"></div>Завантаження...</div>}>
          <Routes>
            {/* localadmin бачить тільки аналітику */}
            {user?.role === 'localadmin' ? (
              <>
                <Route index element={<Analytics user={user} />} />
                <Route path="analytics" element={<Analytics user={user} />} />
                <Route path="*" element={<Analytics user={user} />} />
              </>
            ) : (
              <>
                <Route index element={<Dashboard user={user} />} />
                <Route path="dashboard" element={<Dashboard user={user} />} />
                <Route path="analytics" element={['localadmin', 'superadmin', 'admin', 'trainer'].includes(user?.role) ? <Analytics user={user} /> : <Dashboard user={user} />} />
                <Route path="users" element={user?.role === 'superadmin' ? <UserManager /> : <Dashboard user={user} />} />
                <Route path="cities" element={user?.role === 'superadmin' ? <CityManagement /> : <Dashboard user={user} />} />
                <Route path="departments" element={user?.role === 'superadmin' ? <DepartmentManager /> : <Dashboard user={user} />} />
                <Route path="menu-management" element={user?.role === 'superadmin' ? <MenuManagement /> : <Dashboard user={user} />} />
                <Route path="dishes" element={['superadmin', 'admin'].includes(user?.role) ? <DishManagement /> : <Dashboard user={user} />} />
                <Route path="virtual-desk" element={['superadmin', 'admin'].includes(user?.role) ? <VirtualDesk /> : <Dashboard user={user} />} />
                <Route path="test-results" element={<TestResults user={user} />} />
                <Route path="assignments" element={['superadmin', 'admin', 'trainer'].includes(user?.role) ? <AssignmentManager user={user} /> : <Dashboard user={user} />} />
                <Route path="visual-builder" element={['superadmin', 'admin', 'trainer'].includes(user?.role) ? <VisualGameBuilder /> : <Dashboard user={user} />} />
                <Route path="quiz-builder" element={['superadmin', 'admin', 'trainer'].includes(user?.role) ? <QuizBuilder /> : <Dashboard user={user} />} />
                <Route path="complex-builder" element={['superadmin', 'admin'].includes(user?.role) ? <ComplexTestBuilder /> : <Dashboard user={user} />} />
                <Route path="pairing-builder" element={['superadmin', 'admin', 'trainer'].includes(user?.role) ? <PairingBuilder /> : <Dashboard user={user} />} />
                <Route path="certificates" element={['superadmin', 'admin', 'trainer'].includes(user?.role) ? <CertificateManager /> : <Dashboard user={user} />} />
                <Route path="courses" element={['superadmin', 'admin', 'trainer'].includes(user?.role) ? <CourseManager /> : <Dashboard user={user} />} />
                <Route path="library" element={['superadmin', 'admin', 'trainer'].includes(user?.role) ? <Library /> : <Dashboard user={user} />} />
                <Route path="builders" element={['superadmin', 'admin', 'trainer'].includes(user?.role) ? <BuildersHub hiddenItems={hiddenItems} /> : <Dashboard user={user} />} />
                <Route path="guide" element={<UserGuide user={user} />} />
                <Route path="settings" element={['superadmin', 'admin'].includes(user?.role) ? <Settings user={user} /> : <Dashboard user={user} />} />
                <Route path="store" element={['superadmin', 'admin', 'trainer'].includes(user?.role) ? <StoreManager user={user} /> : <Dashboard user={user} />} />
                <Route path="students" element={['superadmin', 'admin', 'trainer'].includes(user?.role) ? <StudentManager user={user} /> : <Dashboard user={user} />} />
                <Route path="students/:id" element={['superadmin', 'admin', 'trainer'].includes(user?.role) ? <StudentProfile user={user} /> : <Dashboard user={user} />} />
                <Route path="*" element={<Dashboard user={user} />} />
              </>
            )}
          </Routes>
        </Suspense>
      </main>
    </div>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => !!localStorage.getItem('token')
  );
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });

  const [theme, setTheme] = useState(
    () => localStorage.getItem('theme') || 'dark'
  );

  const [hiddenItems, setHiddenItems] = useState([]);

  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  React.useEffect(() => {
    const fetchMenuConfig = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token || !isAuthenticated) {
          setHiddenItems([]);
          return;
        }
        const res = await axios.get(`${API_URL}/menu-config/my`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHiddenItems(res.data.hiddenItems || []);
      } catch (err) {
        console.error('Failed to fetch menu config in App:', err);
      }
    };
    fetchMenuConfig();
  }, [isAuthenticated, user]);

  const toggleTheme = () => {
    setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));
  };

  const handleLogin = (token, userData) => {
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
  };

  return (
    <Router>
      <ToastProvider>
        <ConfirmProvider>
          <Suspense fallback={<div className="dashboard-loading"><div className="loading-spinner"></div>Завантаження...</div>}>
            <Routes>
              <Route path="/test/:hash" element={<StudentTest />} />
              <Route path="/game/:hash" element={<GamePlay />} />
              <Route path="/quiz/:hash" element={<QuizPlay />} />
              <Route path="/multi-test/:hash" element={<MultiDeskTest />} />
              <Route path="/complex/:hash" element={<ComplexTestPlay />} />
              <Route path="/pizza-test/:hash" element={<PizzaTestPlay />} />
              <Route path="/pizza/play/:hash" element={<PizzaTestPlay />} />
              <Route path="/verify-certificate/:id" element={<CertificateVerify />} />
              <Route path="/verify-certificate" element={<CertificateLookup />} />
              <Route path="/inactive" element={<InactiveTest />} />
              <Route path="/register/:hash" element={<StudentRegister onLogin={handleLogin} />} />
              <Route path="/course-play/:courseId" element={isAuthenticated ? <CoursePlayer /> : <Login onLogin={handleLogin} />} />
              <Route
                path="/*"
                element={
                  isAuthenticated
                    ? (user?.role === 'student' ? <StudentDashboard onLogout={handleLogout} user={user} theme={theme} toggleTheme={toggleTheme} /> : <AdminPanel onLogout={handleLogout} user={user} theme={theme} toggleTheme={toggleTheme} hiddenItems={hiddenItems} />)
                    : <Login onLogin={handleLogin} />
                }
              />
            </Routes>
          </Suspense>
        </ConfirmProvider>
      </ToastProvider>
    </Router>
  );
}

export default App;

